<button
  type="button"
  id="view-selection-button"
  class="neptune-button-white box-border h-12 rounded px-[15px] py-[11px] font-normal"
  [attr.aria-expanded]="isOpen"
  aria-haspopup="dialog"
  (click)="open()">
  <img src="assets/shared-icons/neptune-icon-blue-eye-open.svg" alt="" aria-hidden="true" />
  View Selection
</button>

<div class="neptune-modal-cover box-border overscroll-contain px-4 sm:px-6" *ngIf="isOpen">
  <section
    #viewSelectionDialog
    id="view-selection-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="view-selection-modal-title"
    tabindex="-1"
    class="box-border flex max-h-[95vh] w-full max-w-[789px] flex-col gap-4 overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg bg-white p-4 focus:outline-none sm:gap-6 sm:p-6">

    <!-- Header -->
    <header class="box-border flex h-11 w-full shrink-0 items-center justify-between neptune-border-b-1 border-sky-blue pb-2">
      <div id="view-selection-modal-title" class="m-0 truncate text-xl font-normal leading-9 text-neptune-default sm:text-2xl">
        View Selection
      </div>
      <button
        type="button"
        id="view-selection-modal-close-icon"
        class="flex h-9 w-9 shrink-0 items-center justify-center bg-transparent p-0"
        aria-label="Close View Selection"
        (click)="close()">
        <img src="assets/shared-icons/neptune-icon-black-close.svg" alt="" aria-hidden="true" class="h-9 w-9" />
      </button>
    </header>

    <!-- Selected filter chips -->
    <div *ngIf="isLoading" id="view-selection-chips-loading" class="flex w-full animate-pulse flex-col gap-2" aria-busy="true">
      <div class="flex flex-wrap items-center gap-2">
        <div class="h-8 w-[156px] max-w-full rounded-2xl bg-neptune-subtle"></div>
        <div class="h-8 w-[149px] max-w-full rounded-2xl bg-neptune-subtle"></div>
        <div class="h-8 w-[163px] max-w-full rounded-2xl bg-neptune-subtle"></div>
        <div class="h-8 w-[107px] max-w-full rounded-2xl bg-neptune-subtle"></div>
      </div>
      <div class="h-8 w-[553px] max-w-full rounded-2xl bg-neptune-subtle"></div>
      <div class="h-14 w-full rounded-2xl bg-neptune-subtle"></div>
    </div>

    <div *ngIf="!isLoading" id="view-selection-chips" class="flex w-full flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <span *ngIf="criteria?.division" id="division" class="neptune-filter-selection-chip !w-auto">
          <span class="break-words"><span class="text-neptune-color-fg-muted">Division:</span> {{ criteria?.division }}</span>
        </span>
        <span *ngIf="criteria?.dateRange" id="date-range" class="neptune-filter-selection-chip !w-auto">
          <span class="break-words"><span class="text-neptune-color-fg-muted">Date Range:</span> {{ criteria?.dateRange }}</span>
        </span>
        <span *ngIf="template" id="template" class="neptune-filter-selection-chip !w-auto">
          <span class="break-words"><span class="text-neptune-color-fg-muted">Template:</span> {{ template }}</span>
        </span>
      </div>

      <span *ngIf="criteria?.regions?.length" id="region" class="neptune-filter-selection-chip w-full">
        <span class="break-words"><span class="text-neptune-color-fg-muted">Region:</span> {{ criteria?.regions?.join(', ') }}</span>
      </span>

      <span *ngIf="criteria?.countries?.length" id="countries" class="neptune-filter-selection-chip w-full">
        <span class="break-words"><span class="text-neptune-color-fg-muted">Countries:</span> {{ criteria?.countries?.join(', ') }}</span>
      </span>
    </div>

    <!-- Selected groups accordion -->
    <!-- tabindex makes the region focusable so Arrow Up/Down scroll it. -->
    <div
      id="view-selection-groups"
      tabindex="0"
      role="group"
      aria-label="Selected hierarchy"
      class="flex max-h-[min(480px,60vh)] w-full flex-col gap-4 overflow-y-auto overflow-x-hidden pr-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-blue [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-border-gray [&::-webkit-scrollbar]:w-1">

      <div *ngIf="isLoading" id="view-selection-loading" class="flex w-full animate-pulse flex-col gap-4" aria-busy="true">
        <div
          *ngFor="let placeholder of [1, 2, 3]"
          class="box-border h-20 w-full rounded-lg border-1 border-solid border-sky-blue bg-neptune-color-primary-50 p-[15px]">
          <div class="flex h-full items-center justify-between gap-4">
            <div class="flex min-w-0 flex-1 flex-col gap-2">
              <div class="h-5 w-[409px] max-w-full rounded bg-neptune-subtle"></div>
              <div class="h-5 w-[260px] max-w-full rounded bg-neptune-subtle"></div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <div class="hidden h-5 w-[120px] rounded bg-neptune-subtle sm:block lg:w-[171px]"></div>
              <div class="h-6 w-6 rounded bg-neptune-subtle"></div>
            </div>
          </div>
        </div>
      </div>

      <p *ngIf="!isLoading && hasError" id="view-selection-error" class="m-0 py-10 text-center text-sm leading-5 text-neptune-color-red-600">
        Unable to load the selected hierarchy. Please try again.
      </p>

      <p *ngIf="!isLoading && !hasError && !nodes.length" id="view-selection-empty" class="m-0 py-10 text-center text-sm leading-5 text-neptune-color-fg-muted">
        No selection to display.
      </p>

      <div *ngFor="let node of nodes;let i = index; trackBy: trackById" class="w-full">
        <button
          type="button"
          id="view-selection-group"
          class="sticky top-0 z-20 box-border flex w-full flex-col items-start justify-between gap-2 border-1 border-solid border-sky-blue bg-neptune-color-primary-50 p-[15px] text-left disabled:cursor-default sm:flex-row sm:items-center sm:gap-4"
          [ngClass]="isExpanded(node) ? 'rounded-t-lg' : 'rounded-lg'"
          [disabled]="!canExpandNode(node)"
          [attr.aria-expanded]="canExpandNode(node) ? isExpanded(node) : null"
          [attr.aria-controls]="canExpandNode(node) ? node.id + '-panel' : null"
          [attr.aria-label]="canExpandNode(node) ? (isExpanded(node) ? 'Collapse ' : 'Expand ') + node.number + ' ' + node.name : null"
          (click)="toggle(node)">
          <span class="flex w-full min-w-0 flex-1 flex-col gap-2 text-sm leading-5">
            <span class="flex flex-wrap items-center gap-1 font-bold text-neptune-color-primary-500">
              <span id="hierarchy-level-name-{{i}}" *ngIf="node.number">{{ node.hierarchyLevelName }} :</span>
              <span id="node-details-{{i}}" class="break-words">{{ node.number }} {{ node.name }}</span>
            </span>
            <span class="break-words font-semibold text-neptune-neutral">{{ criteriaSummary(node) }}</span>
          </span>
          <span class="flex w-full shrink-0 items-center justify-between gap-2 sm:w-auto sm:justify-end">
            <span *ngIf="summary(node)" id="summary-count-{{i}}" class="text-right text-sm font-normal leading-5 text-neptune-color-fg-muted">
              {{ summary(node) }}
            </span>
            <img
              *ngIf="canExpandNode(node)"
              src="assets/shared-icons/neptune-icon-blue-chevron-down.svg"
              alt=""
              aria-hidden="true"
              class="h-6 w-6 transition-transform"
              [class.rotate-180]="isExpanded(node)" />
          </span>
        </button>

        <div
          *ngIf="isExpanded(node)"
          [id]="node.id + '-panel'"
          class="box-border flex w-full flex-col gap-2 rounded-b-lg bg-neptune-color-secondary-100 p-4">

          <!-- 272px = five 48px rows plus four 8px gaps, so a sixth row starts the scroll. -->
          <div
            *ngIf="hasHierarchyChildren(node)"
            id="view-selection-group-children"
            tabindex="0"
            role="group"
            [attr.aria-label]="'Children of ' + node.number"
            class="flex max-h-[272px] w-full flex-col gap-2 overflow-y-auto overflow-x-hidden pr-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-blue [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-border-gray [&::-webkit-scrollbar]:w-1">

            <div *ngFor="let child of getHierarchyChildren(node); let i = index;trackBy: trackById" class="w-full shrink-0">
              <button
                type="button"
                id="view-selection-group-child-{{i}}"
                class="sticky top-0 z-10 box-border flex w-full flex-col items-start justify-between gap-2 border-l-[3px] border-solid border-neptune-color-primary-300 bg-neptune-color-primary-100 py-3 pl-[13px] pr-4 text-left disabled:cursor-default sm:flex-row sm:items-center sm:gap-4"
                [ngClass]="isExpanded(child) ? 'rounded-t-lg' : 'rounded-lg'"
                [disabled]="!canExpandNode(child)"
                [attr.aria-expanded]="canExpandNode(child) ? isExpanded(child) : null"
                [attr.aria-controls]="canExpandNode(child) ? child.id + '-panel' : null"
                [attr.aria-label]="canExpandNode(child) ? (isExpanded(child) ? 'Collapse ' : 'Expand ') + child.number + ' ' + child.name : null"
                (click)="toggle(child)">
                <span class="flex w-full min-w-0 flex-wrap items-center gap-2 text-sm leading-5 sm:w-auto sm:flex-1">
                  <span id="child-hierarchy-level-name-{{i}}" class="shrink-0 font-bold text-neptune-color-primary-500">{{ child.hierarchyLevelName }} :</span>
                  <span id="child-details-{{i}}" class="break-words font-normal text-neptune-color-fg-muted">{{ child.number }} {{ child.name }}</span>
                </span>
                <span class="flex w-full shrink-0 items-center justify-between gap-2 sm:w-auto sm:justify-end">
                  <span id="child-summary-{{i}}" *ngIf="summary(child)" class="text-right text-sm font-normal leading-5 text-neptune-color-fg-muted">
                    {{ summary(child) }}
                  </span>
                  <img
                    *ngIf="canExpandNode(child)"
                    src="assets/shared-icons/neptune-icon-blue-chevron-down.svg"
                    alt=""
                    aria-hidden="true"
                    class="h-6 w-6 transition-transform"
                    [class.rotate-180]="isExpanded(child)" />
                </span>
              </button>

              <div
                *ngIf="isExpanded(child)"
                [id]="child.id + '-panel'"
                class="box-border w-full rounded-b-lg bg-white p-4">
                <ng-container *ngTemplateOutlet="accountList; context: { $implicit: getAccountChildren(child) }"></ng-container>
              </div>
            </div>
          </div>

          <ng-container *ngTemplateOutlet="accountList; context: { $implicit: getAccountChildren(node) }"></ng-container>
        </div>
      </div>
    </div>

    <ng-template #accountList let-accounts>
      <!-- Multi-column so accounts fill top-to-bottom in each column, like Figma. -->
      <div
        *ngIf="accounts.length"
        id="view-selection-accounts"
        class="w-full columns-1 gap-x-2 sm:columns-2 md:columns-3">
        <span id="view-selection-accounts-{{index}}"
          *ngFor="let account of accounts; index as index; trackBy: trackById"
          class="mb-[11px] block break-inside-avoid truncate text-sm font-normal leading-5 text-neptune-neutral last:mb-0"
          [title]="account.number + ' ' + account.name">
          {{ index + 1 }}. {{ account.number }} {{ account.name }}
        </span>
      </div>
    </ng-template>

    <!-- Footer -->
    <footer class="box-border flex h-16 w-full flex-col items-end justify-center neptune-border-t-1 border-neptune-color-neutral-600 pt-4">
      <button
        type="button"
        id="view-selection-modal-close"
        class="flex w-[100px] items-center justify-center gap-2 rounded neptune-color-primary-500 px-4 py-3 text-base font-normal leading-6 text-white"
        (click)="close()">
        Close
      </button>
    </footer>
  </section>
</div>
